/**
 * Color mode debug logging. Enable by:
 *   - URL: add ?theme-ui-color-mode-debug (works after load; no localStorage needed)
 *   - localStorage: setItem('theme-ui-color-mode-debug', '1')
 *   - window: __THEME_UI_COLOR_MODE_DEBUG__ = true
 *
 * Logs color mode, theme colors, and computed CSS variables to help troubleshoot
 * stuck text (e.g. white text on light background when switching to light mode).
 */

const DEBUG_KEY = 'theme-ui-color-mode-debug'
const DEBUG_URL_PARAM = 'theme-ui-color-mode-debug'

export function isColorModeDebugEnabled() {
  if (typeof window === 'undefined') return false
  try {
    if (window.__THEME_UI_COLOR_MODE_DEBUG__ === true) return true
    const params = new URLSearchParams(window.location.search)
    if (params.get(DEBUG_URL_PARAM) !== null) return true
    return localStorage.getItem(DEBUG_KEY) === '1' || localStorage.getItem(DEBUG_KEY) === 'true'
  } catch {
    return false
  }
}

/** Call once on load when URL param is present so user sees debug is active. */
export function logColorModeDebugBanner() {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get(DEBUG_URL_PARAM) !== null) {
      console.log(
        '[theme-ui] Color mode debug enabled via ?theme-ui-color-mode-debug. Toggle theme and check console for [theme-ui color-mode] logs.'
      )
    }
  } catch {
    return void 0
  }
}

/**
 * Log current color mode state and computed styles for troubleshooting.
 * No-op when debug is disabled.
 *
 * @param {string} colorMode - Current Theme UI color mode ('default' | 'dark')
 * @param {object} theme - Theme object from useThemeUI()
 * @param {string} source - Call site label (e.g. 'RootWrapper')
 */
export function logColorModeState(colorMode, theme, source = '') {
  if (!isColorModeDebugEnabled()) return
  try {
    const root = document.documentElement
    const computed = root && typeof window.getComputedStyle === 'function' ? window.getComputedStyle(root) : null
    const textVar = computed?.getPropertyValue('--theme-ui-colors-text')?.trim()
    const bgVar = computed?.getPropertyValue('--theme-ui-colors-background')?.trim()
    const dataAttr = root?.getAttribute('data-theme-ui-color-mode')

    console.groupCollapsed(`[theme-ui color-mode] ${source} | mode=${colorMode} | data-attr=${dataAttr ?? 'none'}`)
    console.log('colorMode', colorMode)
    console.log('theme.colors.text', theme?.colors?.text)
    console.log('theme.colors.background', theme?.colors?.background)
    console.log('theme.rawColors?.background', theme?.rawColors?.background)
    console.log('Computed --theme-ui-colors-text', textVar || '(not set)')
    console.log('Computed --theme-ui-colors-background', bgVar || '(not set)')
    console.log('documentElement.style.backgroundColor', root?.style?.backgroundColor || '(not set)')
    console.groupEnd()
  } catch (e) {
    console.warn('[theme-ui color-mode] debug log failed', e)
  }
}
